import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import { getWantedMatchesForMaterial } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ClipboardList, MapPin, Calendar, Package, ArrowRight } from 'lucide-react';

const formatPrice = (min, max) => {
  if (min == null && max == null) return null;
  const fmt = (v) => `${Number(v).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
  if (min != null && max != null) return `${fmt(min)} – ${fmt(max)}`;
  if (min != null) return `ab ${fmt(min)}`;
  return `bis ${fmt(max)}`;
};

const formatDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

/**
 * Cross-promotion: shows active wanted ads that match the currently viewed
 * material (same category, optional width tolerance). Public — works for
 * anonymous visitors. Each card links to the wanted-ads board so sellers can
 * respond.
 */
export const MatchingWantedAdsSection = ({ materialId }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [ads, setAds] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!materialId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await getWantedMatchesForMaterial(materialId, 4);
        if (cancelled) return;
        setAds(res.data?.ads || []);
        setTotal(res.data?.total || 0);
      } catch {
        if (!cancelled) { setAds([]); setTotal(0); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [materialId]);

  if (loading || !ads.length) return null;

  const handleBrowseAll = () => {
    if (!user) {
      navigate(`/login?next=${encodeURIComponent('/wanted')}`);
      return;
    }
    navigate('/wanted');
  };

  return (
    <section
      className="mt-12 pt-8 border-t border-slate-200 dark:border-slate-800"
      data-testid="matching-wanted-ads-section"
    >
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="w-1 h-6 bg-amber-500 rounded-full" />
            <ClipboardList className="h-5 w-5 text-amber-500" />
            <h2 className="font-manrope text-xl md:text-2xl font-bold tracking-tight">
              {t('material.matchingWanted.heading', 'Passende Gesuche zu diesem Material')}
            </h2>
          </div>
          <p className="text-sm text-muted-foreground ml-3 pl-3">
            {total === 1
              ? t('material.matchingWanted.subOne', '1 Käufer sucht gerade nach diesem Material.')
              : t('material.matchingWanted.sub', '{{count}} Käufer suchen gerade nach diesem Material.', { count: total })}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5 border-orange-300 text-orange-600 hover:bg-orange-50"
          onClick={handleBrowseAll}
          data-testid="matching-wanted-ads-browse-btn"
        >
          {t('material.matchingWanted.browseAll', 'Alle Gesuche ansehen')}
          <ArrowRight className="h-3.5 w-3.5" />
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {ads.map((ad) => {
          const priceStr = formatPrice(ad.price_min_eur, ad.price_max_eur);
          return (
            <Card
              key={ad.wanted_id}
              className="hover:shadow-md hover:border-orange-300 transition-all cursor-pointer"
              onClick={handleBrowseAll}
              data-testid={`matching-wanted-card-${ad.wanted_id}`}
            >
              <CardContent className="p-4">
                <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 text-[10px] mb-2">
                  {ad.category_name}
                </Badge>
                <h3 className="font-semibold text-sm leading-tight line-clamp-2 mb-2">
                  {ad.material_type_description}
                </h3>
                <div className="space-y-1 text-xs text-muted-foreground">
                  <div className="flex items-center gap-1.5">
                    <Package className="h-3 w-3 text-blue-500 shrink-0" />
                    <span>{ad.min_quantity} {ad.quantity_unit}</span>
                  </div>
                  {priceStr && (
                    <div className="flex items-center gap-1.5 font-medium text-emerald-700 dark:text-emerald-400">
                      <span className="inline-block w-3 text-center">€</span>
                      <span>{priceStr}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-1.5">
                    <MapPin className="h-3 w-3 text-green-500 shrink-0" />
                    <span>{[ad.poster_postal_code, ad.poster_country].filter(Boolean).join(', ') || '—'}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Calendar className="h-3 w-3 text-orange-500 shrink-0" />
                    <span>{t('material.matchingWanted.validUntil', 'bis')} {formatDate(ad.valid_until)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </section>
  );
};
